import { useState, useMemo, useCallback } from 'react';
import type { ArticleCollectionWithItems } from '@/types/article-collection';
import { buildItemTree } from '@/types/article-collection';
import { AddToCollectionModal } from './AddToCollectionModal';
import { AddHeadingModal } from './AddHeadingModal';
import { CollectionItemList } from './CollectionItemList';
import {
  useDeleteCollectionItem,
  useReorderCollectionItems,
  useAddCollectionItem,
} from '@/queries/article-collections';
import { useUserTier } from '@/queries/subscriptions';
import PlusIcon from '@/assets/common/plus.svg?react';
import FolderIcon from '@/assets/common/folder.svg?react';
import styles from './CollectionContents.module.scss';

interface CollectionContentsProps {
  collection: ArticleCollectionWithItems;
}

export function CollectionContents({ collection }: CollectionContentsProps) {
  const [showAddArticlesModal, setShowAddArticlesModal] = useState(false);
  const [showAddHeadingModal, setShowAddHeadingModal] = useState(false);
  const [selectedParentId, setSelectedParentId] = useState<string | null>(null);

  const userTier = useUserTier();
  const hasReadingModeAccess = userTier !== 'foundation';

  const deleteItem = useDeleteCollectionItem();
  const reorderItems = useReorderCollectionItems();
  const addItem = useAddCollectionItem();

  // Build tree structure from flat items
  const itemTree = useMemo(
    () => buildItemTree(collection.items || []),
    [collection.items]
  );

  const handleAddHeading = (parentId: string | null = null) => {
    setSelectedParentId(parentId);
    setShowAddHeadingModal(true);
  };

  const handleAddArticles = (parentId: string | null = null) => {
    setSelectedParentId(parentId);
    setShowAddArticlesModal(true);
  };

  const handleDeleteItem = useCallback((itemId: string, itemTitle: string) => {
    const itemType = collection.items?.find(i => i.id === itemId)?.item_type;
    const message = itemType === 'heading'
      ? `Are you sure you want to delete "${itemTitle}"? This will also delete all items nested inside it.`
      : `Remove "${itemTitle}" from this collection?`;

    if (confirm(message)) {
      deleteItem.mutate({
        collectionId: collection.id,
        itemId,
      });
    }
  }, [collection.id, collection.items, deleteItem]);

  const handleReorder = useCallback(
    async (items: Array<{ id: string; parent_id: string | null; order_index: number }>) => {
      await reorderItems.mutateAsync({
        collectionId: collection.id,
        data: { items },
      });
    },
    [collection.id, reorderItems]
  );

  const handleCreateHeading = useCallback(
    async (title: string) => {
      await addItem.mutateAsync({
        collectionId: collection.id,
        data: {
          item_type: 'heading',
          title,
          parent_id: selectedParentId,
        },
      });
      setShowAddHeadingModal(false);
      setSelectedParentId(null);
    },
    [collection.id, selectedParentId, addItem]
  );

  const isEmpty = !collection.items || collection.items.length === 0;

  return (
    <div className={styles.collectionContents}>
      <div className={styles.collectionContents__header}>
        <div className={styles.collectionContents__titleSection}>
          <h1 className={styles.collectionContents__title}>{collection.name}</h1>
          {collection.description && (
            <p className={styles.collectionContents__description}>
              {collection.description}
            </p>
          )}
        </div>

        <div className={styles.collectionContents__actions}>
          <button
            className={`${styles.collectionContents__button} ${styles['collectionContents__button--secondary']}`}
            onClick={() => handleAddHeading(null)}
          >
            <PlusIcon />
            Add Heading
          </button>
          <button
            className={`${styles.collectionContents__button} ${styles['collectionContents__button--primary']}`}
            onClick={() => handleAddArticles(null)}
          >
            <PlusIcon />
            Add Articles
          </button>
        </div>
      </div>

      {isEmpty ? (
        <div className={styles.collectionContents__emptyState}>
          <FolderIcon className={styles.collectionContents__emptyIcon} />
          <h3 className={styles.collectionContents__emptyTitle}>
            This collection is empty
          </h3>
          <p className={styles.collectionContents__emptyText}>
            Add articles or create headings to organize your content.
          </p>
        </div>
      ) : (
        <CollectionItemList
          items={itemTree}
          collectionId={collection.id}
          hasReadingModeAccess={hasReadingModeAccess}
          onReorder={handleReorder}
          onDeleteItem={handleDeleteItem}
          onAddHeading={handleAddHeading}
          onAddArticles={handleAddArticles}
        />
      )}

      <AddHeadingModal
        isOpen={showAddHeadingModal}
        onClose={() => {
          setShowAddHeadingModal(false);
          setSelectedParentId(null);
        }}
        onSubmit={handleCreateHeading}
        isSubmitting={addItem.isPending}
      />

      <AddToCollectionModal
        isOpen={showAddArticlesModal}
        onClose={() => {
          setShowAddArticlesModal(false);
          setSelectedParentId(null);
        }}
        collectionId={collection.id}
        parentId={selectedParentId}
      />
    </div>
  );
}
